import { Link, useParams, Navigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, siteConfig } from '@/mocks/navigation';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/articles" replace />;
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 2);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    url: `${siteConfig.url}/article/${slug}`,
    inLanguage: 'zh-CN',
    author: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
      url: siteConfig.url,
    },
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.url}/article/${slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} | ${siteConfig.name}`}
        description={article.excerpt}
        keywords={`${article.title},夜间导航,夜色导航,${article.categoryName}`}
        canonicalUrl={`${siteConfig.url}/article/${slug}`}
        ogType="article"
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-50 text-foreground-500">
        <Navbar />

        <main className="pt-20">
          <article className="max-w-3xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '导航指南', path: '/articles' },
                { label: article.categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <header className="py-8 md:py-12">
              <div className="flex items-center gap-3 mb-5">
                <Link
                  to={`/category/${article.category}`}
                  className="bg-accent-500/10 text-accent-600 text-xs font-medium px-3 py-1.5 rounded-full hover:bg-accent-500/20 transition-colors duration-200 cursor-pointer"
                >
                  {article.categoryName}
                </Link>
                <span className="text-foreground-400 text-xs">{article.date}</span>
                <span className="text-foreground-400 text-xs">{article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-foreground-400 text-sm md:text-base leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            {/* Featured Image */}
            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose-custom pb-12"
              dangerouslySetInnerHTML={{
                __html: article.content
                  .replace(/<h2>/g, '<h2 class="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">')
                  .replace(/<h3>/g, '<h3 class="text-lg font-semibold text-foreground-800 mt-8 mb-3">')
                  .replace(/<p>/g, '<p class="text-foreground-500 text-sm md:text-base leading-relaxed mb-4">')
                  .replace(/<strong>/g, '<strong class="text-foreground-700 font-semibold">')
                  .replace(/<ul>/g, '<ul class="list-disc pl-5 space-y-2 mb-4 text-sm md:text-base text-foreground-500">')
                  .replace(/<li>/g, '<li class="leading-relaxed">'),
              }}
            ></div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="pb-16 border-t border-background-200/50 pt-10">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group flex items-start gap-4 bg-background-100 border border-background-200/50 rounded-xl p-4 transition-all duration-300 hover:border-primary-500/20 cursor-pointer"
                    >
                      <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-foreground-700 font-medium text-sm group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                          {ra.title}
                        </h3>
                        <span className="text-foreground-400 text-xs mt-1.5 block">
                          {ra.date}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Back Link */}
            <div className="pb-16">
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 text-foreground-400 hover:text-foreground-600 text-sm transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回文章列表
              </Link>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}